// react
import { useCallback } from 'react';

// store
import {
  useListOrganiseEntriesQuery,
  useGetCalendarForCareCircleQuery,
} from 'store/api/organise';

// types
import { OrganiseEntryStatus } from '@karehero/models';
import { useGetCareCircleQuery } from 'store/api/careCircle';
import { WarmOrange } from '@karehero/llama/theme/mui/color';
import { useOrganiserActionsWithTracking } from './useTrackedOrganiserActions';

export const useAdminOrganiserActions = (
  careCircleID: string,
  options?: { skip?: boolean },
) => {
  const { changeStatus, ...actionsWithTracking } =
    useOrganiserActionsWithTracking();

  const { data: calendar } = useGetCalendarForCareCircleQuery(careCircleID, {
    skip: options?.skip,
  });
  const { data: entries, isLoading } = useListOrganiseEntriesQuery(
    { calendarId: calendar?.id || '' },
    {
      skip: options?.skip || !calendar,
    },
  );
  const { data: careCircle } = useGetCareCircleQuery(careCircleID || '', {
    skip: !careCircleID,
  });

  const findRawEntry = (entryId: string) => {
    return entries?.find((e) => e.id === entryId);
  };

  const handleStatusChange = useCallback(
    async (entryId: string, status: OrganiseEntryStatus) => {
      const entry = findRawEntry(entryId);

      if (!entry) return;

      changeStatus(entry, status);
    },
    [findRawEntry, changeStatus],
  );

  return {
    ...actionsWithTracking,
    isLoading,
    entries:
      entries?.map((entry) => {
        const assignee = careCircle?.careCircleMembers?.find(
          (member) => member.account.id === entry.assigneeAccountId,
        );
        return {
          ...entry,
          assigneeName: assignee?.account?.fullName,
          assigneeProfile: {
            ...assignee,
            id: assignee?.account.id || '',
            name: assignee?.account?.fullName || '',
          },
          careCircleColor: WarmOrange[500],
          careCircleName: careCircle?.careRecipientAccount.fullName || '',
        };
      }) || [],
    calendars: [
      {
        calendarId: calendar?.id || '',
        label: careCircle?.careRecipientAccount.fullName || '',
        members:
          careCircle?.careCircleMembers?.map((member) => ({
            id: member.account.id || '',
            name: member.account.fullName || '',
          })) || [],
      },
    ],
    findRawEntry,
    changeStatus: handleStatusChange,
  };
};
